import type { AudioEngine } from "../core/AudioEngine";
import type { AudioMixer } from "../mixer/AudioMixer";
import { LoopPlayer } from "../mixer/LoopPlayer";
import type { AudioBankLoader } from "../banks/AudioBankLoader";
import type { FeatureAudioState } from "../ports/types";
import { resolveBus, resolveGain } from "../policy/soundResolution";

/** Cancels a scheduled callback. */
type Cancel = () => void;

/** How close to the hand-over point the intro timer may fire and still hand over (timer jitter). */
const HAND_OVER_TOLERANCE_MS = 10;

/** Tries a bed's download gets in all, and the pause before each retry. */
const BED_ATTEMPTS = 3;
const BED_RETRY_PAUSE_MS = 2_000;

/**
 * Every looping sound: the music bed (by feature state, else the configured base; an intro stinger
 * hands over to its loop), the ambient bed, the keyed SFX loops (reel roll, anticipation roll) and the
 * big-win count loop. Owns the two crossfading loop controllers; the mixer, the engine and the loader
 * are given.
 */
export class AudioLoops {
  private readonly music: LoopPlayer;
  private readonly ambient: LoopPlayer;
  private featureState: FeatureAudioState = null;
  private pendingMusicIntro: { stop: () => void; clearLoopTimer: Cancel } | null = null;
  /**
   * The music loop the game last asked for — a panel's track, the loop a stinger leads into, the
   * feature-state bed — whether or not the engine could start it. A foreground return and the
   * re-unlock after a hide put it back. It survives every stop (a `pagehide` teardown, a start
   * refused because the resume left the context not running), so a refused restart forgets nothing
   * and a request made while starts are refused is not lost (review 2026-09-27 #25). Null until the
   * first request.
   */
  private requestedMusic: string | null = null;
  /**
   * The ambient bed the game last asked for: its `ambientTrackId` until a `setAmbientTrack` changes
   * it (null: turned off). The unlock-time start, a foreground return and the re-unlock after a hide
   * put it back; like the music's, a refused start forgets nothing (review 2026-09-27 #55).
   */
  private requestedAmbient: string | null;
  private bigWinCountLoop: { stop: () => void; gainNode: GainNode } | null = null;
  /** Keyed SFX loops: one seamless loop per sound id on the sound's own bus; no fades, instant gain changes. */
  private readonly sfxLoops = new Map<string, { stop: () => void; gainNode: GainNode; baseGain: number }>();

  public constructor(
    private readonly deps: {
      ctx: AudioContext;
      engine: AudioEngine;
      mixer: AudioMixer;
      loader: AudioBankLoader;
      /** The music bed outside a feature state (`GameAudioOverrides.baseMusicId`, default the shell bank's). */
      baseMusicId: string;
      /** The game's music per feature state and its ambient bed, if any. */
      featureStateMusicMap?: Partial<Record<string, string>> | null;
      ambientTrackId?: string | null;
      /** A sound id with no asset (counted as a drop by the caller). */
      onMissingSound?: (soundId: string) => void;
      devLog?: (m: string) => void;
      /** Wall-clock timers: the pause before a bed's retry, and the intro → loop hand-over (which re-checks the audio clock each time it fires). */
      schedule?: (fn: () => void, ms: number) => Cancel;
    },
  ) {
    this.music = new LoopPlayer(deps.ctx);
    this.ambient = new LoopPlayer(deps.ctx);
    this.requestedAmbient = deps.ambientTrackId ?? null;
  }

  public get currentMusicId(): string | null {
    return this.music.getCurrentLoopId();
  }

  public get currentAmbientId(): string | null {
    return this.ambient.getCurrentLoopId();
  }

  // ── Music ────────────────────────────────────────────────────────────────────────────────

  /** The feature state selects the music bed; applied on the next `playBaseMusic`. */
  public setFeatureState(state: FeatureAudioState): void {
    this.featureState = state;
  }

  /** The bed for the current feature state (the base track when none is mapped). */
  public playBaseMusic(): void {
    this.playMusic(this.resolveBaseMusicId(), 300);
  }

  public playMusic(loopSoundId: string, fadeMs: number): void {
    this.requestedMusic = loopSoundId;
    this.cancelPendingIntro();
    this.startMusicLoopCrossfade(loopSoundId, fadeMs);
  }

  /**
   * An intro stinger handing over to its loop: the loop starts ramping in BEFORE the intro's buffer
   * ends, so it is audible at full volume well before the intro's source finishes. Two values, tuned
   * independently: LEAD = how long before `buffer.duration` the loop starts (generous, because
   * stingers often carry trailing silence / reverb tails); FADE = how long the loop takes to ramp
   * 0 → target (short, so it reaches full volume quickly). Net: the loop is at full volume from
   * (lead − fade) seconds before the buffer's end onward.
   */
  public playMusicWithIntro(introId: string, loopId: string): void {
    this.requestedMusic = loopId;
    this.cancelPendingIntro();
    const { engine, mixer, loader } = this.deps;

    const introBuffer = loader.getBuffer(introId);
    if (!introBuffer) {
      void loader.preloadOne(introId);
      this.deps.devLog?.(`[audio] music-intro buffer missing id=${introId} — falling through to loop`);
      this.playMusic(loopId, 300);
      return;
    }

    // Fade the current loop out while the intro takes over the music bus.
    this.music.stopAll(300);

    const introGain = resolveGain(loader.getAsset(introId)?.defaultOpts?.gain);

    // Sentinel for the `onEnded` closure + the overlap timer to detect interruption — see
    // `cancelPendingIntro` for the null-first invariant.
    let record: { stop: () => void; clearLoopTimer: Cancel } | null = null;

    const oneShot = engine.playOneShot({
      id: introId,
      buffer: introBuffer,
      destination: mixer.getBusInputNode("music"),
      priority: 100,
      protectFromSteal: true,
      stopOnSlam: false,
      gain: introGain,
      onEnded: () => {
        // The loop was already scheduled by the overlap timer — just clear the state. A superseded
        // intro sees `pendingMusicIntro !== record` and does nothing.
        if (this.pendingMusicIntro !== record) return;
        this.pendingMusicIntro = null;
      },
    });

    if (!oneShot) {
      // Engine locked or voice steal failed — skip the intro, take the loop.
      this.playMusic(loopId, 0);
      return;
    }

    const INTRO_TO_LOOP_LEAD_SECONDS = 1.5;
    const INTRO_TO_LOOP_FADE_SECONDS = 0.5;
    const introDurationMs = introBuffer.duration * 1000;
    const loopStartDelayMs = Math.max(0, introDurationMs - INTRO_TO_LOOP_LEAD_SECONDS * 1000);
    const overlapFadeMs = Math.min(INTRO_TO_LOOP_FADE_SECONDS * 1000, introDurationMs);

    // The hand-over point is on the AUDIO clock, which stops while the page is hidden (the context
    // is suspended with the intro in it). Each time the timer comes due it checks how much audio time
    // is still to go and waits that long again. Until 2026-09-26 a plain wall-clock timer kept
    // counting through a hide, started the loop while the intro was paused, and the rest of the intro
    // played over it on the return (user, 2026-09-26).
    const handOverAt = this.deps.ctx.currentTime + loopStartDelayMs / 1000;
    let cancelTimer: Cancel = () => {};
    const waitForHandOver = (): void => {
      if (this.pendingMusicIntro !== record) return; // cancelled or superseded
      const remainingMs = (handOverAt - this.deps.ctx.currentTime) * 1000;
      if (remainingMs > HAND_OVER_TOLERANCE_MS) {
        cancelTimer = this.schedule(waitForHandOver, remainingMs);
        return;
      }
      this.startMusicLoopCrossfade(loopId, overlapFadeMs);
    };
    cancelTimer = this.schedule(waitForHandOver, loopStartDelayMs);
    const clearLoopTimer: Cancel = () => cancelTimer();

    record = { stop: oneShot.stop, clearLoopTimer };
    this.pendingMusicIntro = record;
  }

  /**
   * Cancels an in-flight music intro. Null-first ordering: the source's `onEnded` (asynchronous,
   * after the node stops) sees `pendingMusicIntro !== record` and short-circuits, so it never
   * schedules its loop after the caller has already picked a different music target.
   */
  public cancelPendingIntro(): void {
    if (!this.pendingMusicIntro) return;
    const prior = this.pendingMusicIntro;
    this.pendingMusicIntro = null;
    prior.clearLoopTimer();
    prior.stop();
  }

  // ── Ambient ──────────────────────────────────────────────────────────────────────────────

  public setAmbient(loopSoundId: string | null, fadeMs: number): void {
    this.requestedAmbient = loopSoundId;
    if (loopSoundId === null) {
      this.ambient.stopAll(fadeMs);
      return;
    }
    this.ambient.crossfadeTo({
      id: loopSoundId,
      fadeMs,
      targetGain: resolveGain(this.deps.loader.getAsset(loopSoundId)?.defaultOpts?.gain),
      startLoop: () => this.startLoopOn("ambient", loopSoundId),
    });
  }

  /**
   * The music and ambient beds at the first unlock, each the moment its own file is in — neither
   * waits for the other or for any other sound. The music bed is not forced over music that started
   * while it downloaded (a panel's track), and the ambient bed that starts is the one last asked for.
   */
  public async startBedsWhenLoaded(): Promise<void> {
    const ambientId = this.deps.ambientTrackId;
    await Promise.all([
      this.loadBed(this.resolveBaseMusicId()).then((loaded) => {
        if (loaded && this.pendingMusicIntro === null && this.music.getCurrentLoopId() === null) this.playBaseMusic();
      }),
      ambientId
        ? this.loadBed(ambientId).then((loaded) => {
            if (loaded) this.restoreAmbient();
          })
        : Promise.resolve(),
    ]);
  }

  /**
   * A bed's file, with two more tries after a short pause when a download fails or freezes (user,
   * 2026-09-26): until then one bad download meant no music for the whole visit. True once it is in.
   */
  private async loadBed(soundId: string): Promise<boolean> {
    for (let attempt = 1; ; attempt++) {
      await this.deps.loader.preloadOne(soundId);
      if (this.deps.loader.getBuffer(soundId)) return true;
      if (attempt >= BED_ATTEMPTS) return false;
      await new Promise<void>((resolve) => this.schedule(resolve, BED_RETRY_PAUSE_MS));
    }
  }

  /** The ambient bed the game last asked for, when there is one (review 2026-09-27 #55). */
  public restoreAmbient(): void {
    if (!this.requestedAmbient) return;
    this.setAmbient(this.requestedAmbient, 300);
  }

  // ── Keyed SFX loops ──────────────────────────────────────────────────────────────────────

  /** Start a keyed loop; idempotent while it runs. */
  public startSfxLoop(soundId: string): void {
    if (this.sfxLoops.has(soundId)) return;
    const { engine, mixer, loader } = this.deps;
    const asset = loader.getAsset(soundId);
    if (!asset) {
      this.deps.onMissingSound?.(soundId);
      this.deps.devLog?.(`[audio] missing sfx-loop soundId: ${soundId}`);
      return;
    }
    const buffer = loader.getBuffer(soundId);
    if (!buffer) {
      void loader.preloadOne(soundId);
      return;
    }
    const loop = engine.createSeamlessLoop({ id: soundId, buffer, destination: mixer.getBusInputNode(resolveBus(asset.category)) });
    if (!loop) return;
    const baseGain = resolveGain(asset.defaultOpts?.gain) ?? 1;
    loop.gainNode.gain.value = baseGain;
    this.sfxLoops.set(soundId, { stop: loop.stop, gainNode: loop.gainNode, baseGain });
  }

  public stopSfxLoop(soundId: string): void {
    const entry = this.sfxLoops.get(soundId);
    if (!entry) return;
    entry.stop();
    this.sfxLoops.delete(soundId);
  }

  public setSfxLoopGainScale(soundId: string, scale: number): void {
    const entry = this.sfxLoops.get(soundId);
    if (!entry) return;
    const safeScale = Number.isFinite(scale) && scale >= 0 ? scale : 1;
    entry.gainNode.gain.value = entry.baseGain * safeScale;
  }

  // ── The big-win count loop ───────────────────────────────────────────────────────────────

  /** Starts the count loop on `soundId`, the sound `bigwin.count_start` resolved to; idempotent while it runs. */
  public startBigWinCountLoop(soundId: string): void {
    if (this.bigWinCountLoop) return;
    const { engine, mixer, loader } = this.deps;
    const buffer = loader.getBuffer(soundId);
    if (!buffer) {
      void loader.preloadOne(soundId);
      return;
    }
    const loop = engine.createSeamlessLoop({ id: soundId, buffer, destination: mixer.getBusInputNode("sfx") });
    if (!loop) return;
    const gain = resolveGain(loader.getAsset(soundId)?.defaultOpts?.gain);
    if (typeof gain === "number") loop.gainNode.gain.value = gain;
    this.bigWinCountLoop = { stop: loop.stop, gainNode: loop.gainNode };
  }

  public stopBigWinCountLoop(): void {
    if (!this.bigWinCountLoop) return;
    this.bigWinCountLoop.stop();
    this.bigWinCountLoop = null;
  }

  // ── Lifecycle hooks ──────────────────────────────────────────────────────────────────────

  /**
   * Puts the music back when the page returns to the foreground, and again on the gesture that
   * re-unlocks the engine after a hide: the loop the game last ASKED for (`requestedMusic`) — a
   * panel's override, or the loop a stinger was leading into — never simply the feature-state bed.
   * A start the engine refused (the resume left the context not running) forgets nothing: the
   * re-unlock puts the same request back (review 2026-09-27 #25).
   *
   * `fresh: false`, a short hide: the engine only suspended the AudioContext, so every source resumed
   * exactly where it was, stinger included, and there is nothing to restart — unless what plays is
   * not what was asked for (a request refused while the context was not running), which crossfades
   * in. Until 2026-09-23 this path crossfaded to the feature-state bed, which replaced a panel's
   * music and hard-stopped its stinger mid-phrase, and the next gesture did it again (review finding 11).
   *
   * `fresh: true`, the sources are gone (a long background, a `pagehide`): the requested loop
   * restarts. A stinger that was mid-phrase is not replayed — its loop starts instead (user,
   * 2026-09-23: straight to the music).
   */
  public restoreMusic(fresh: boolean): void {
    const target = this.requestedMusic ?? this.resolveBaseMusicId();
    if (!fresh) {
      if (this.pendingMusicIntro !== null || this.music.getCurrentLoopId() === target) return;
      this.startMusicLoopCrossfade(target, 300);
      return;
    }
    this.cancelPendingIntro();
    this.startMusicLoopCrossfade(target, 300);
  }

  /** Hard-stop the music and ambient beds synchronously (page hide; a fresh foreground). */
  public stopBedsImmediate(): void {
    this.music.stopAllImmediate();
    this.ambient.stopAllImmediate();
  }

  // ── Internals ────────────────────────────────────────────────────────────────────────────

  private resolveBaseMusicId(): string {
    if (this.featureState !== null) {
      const mapped = this.deps.featureStateMusicMap?.[this.featureState];
      if (mapped) return mapped;
    }
    return this.deps.baseMusicId;
  }

  /** The crossfade to a music loop without touching the pending intro (the intro's overlap timer uses it too). */
  private startMusicLoopCrossfade(loopSoundId: string, fadeMs: number): void {
    this.music.crossfadeTo({
      id: loopSoundId,
      fadeMs,
      targetGain: resolveGain(this.deps.loader.getAsset(loopSoundId)?.defaultOpts?.gain),
      startLoop: () => this.startLoopOn("music", loopSoundId),
    });
  }

  private startLoopOn(bus: "music" | "ambient", soundId: string): { stop: () => void; gainNode: GainNode } | null {
    const buffer = this.deps.loader.getBuffer(soundId);
    if (!buffer) {
      void this.deps.loader.preloadOne(soundId);
      return null;
    }
    const loop = this.deps.engine.createSeamlessLoop({ id: soundId, buffer, destination: this.deps.mixer.getBusInputNode(bus) });
    return loop ? { stop: loop.stop, gainNode: loop.gainNode } : null;
  }

  private schedule(fn: () => void, ms: number): Cancel {
    if (this.deps.schedule) return this.deps.schedule(fn, ms);
    const id = setTimeout(fn, ms);
    return () => clearTimeout(id);
  }
}
